import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPhone, FiMail, FiChevronDown, FiMenu, FiX, FiInstagram, FiYoutube } from 'react-icons/fi';
import HeaderLogo from '../assets/Header_Transparent.png';
import API_BASE from '../config';

const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    {
        label: 'Services', path: '/services', dropdown: [
            { label: 'End to End Solutions', id: 'interior-design' },
            { label: 'Modular Kitchen', id: 'modular-kitchen' },
            { label: 'Living Room Design', id: 'living-room' },
            { label: 'Bedroom Design', id: 'bedroom-design' },
            { label: 'Office Interior', id: 'office-interior' },
        ]
    },
    {
        label: 'Design Ideas', path: '/design-ideas', dropdown: [
            { label: 'Living Room Ideas', id: 'living-room' },
            { label: 'Bedroom Ideas', id: 'bedroom' },
            { label: 'Kitchen Ideas', id: 'kitchen' },
            { label: 'Office Design Ideas', id: 'office' },
            { label: 'Small Space Ideas', id: 'small-space' },
        ]
    },
    {
        label: 'Cities', path: '/cities', dropdown: [
            { label: 'Hyderabad', id: 'hyderabad' },
            { label: 'Nizamabad', id: 'nizamabad' },
            { label: 'Bangalore', id: 'bangalore' },
            { label: 'Vizag', id: 'vizag' },
            { label: 'Puttaparthi', id: 'puttaparthi' },
            { label: 'Anantapur', id: 'anantapur' },
            { label: 'Vijayawada', id: 'vijayawada' },
            { label: 'Rajahmundry', id: 'rajahmundry' },
        ]
    },
    { label: 'Our Projects', path: '/projects' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact Us', path: '/contact' },
];

export default function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [cities, setCities] = useState([]);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        fetch(`${API_BASE}/cities.php`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') setCities(data.cities || []);
            });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setOpenDropdown(null);
    }, [location]);

    // Create dynamic navLinks
    const dynamicNavLinks = navLinks.map(link => {
        if (link.label === 'Cities') {
            // Get labels of hardcoded cities
            const hardcodedLabels = link.dropdown.map(d => d.label);
            
            // Combine with cities from API, ensuring no duplicates and preserving order
            const combinedCities = [...hardcodedLabels];
            cities.forEach(city => {
                if (!hardcodedLabels.includes(city)) {
                    combinedCities.push(city);
                }
            });

            return {
                ...link,
                dropdown: combinedCities.map(city => ({
                    label: city,
                    id: city.toLowerCase().replace(/\s+/g, '-')
                }))
            };
        }
        return link;
    });

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-2xl' : ''}`}>
            {/* Top Bar */}
            <div className="bg-black py-1.5 px-4">
                <div className="max-w-7xl mx-auto flex justify-between items-center text-xs text-white">
                    <div className="flex items-center gap-3">
                        <a href="tel:+919959365158" className="flex items-center gap-1 hover:text-[#2E63E8] transition-colors">
                            <FiPhone size={12} />
                            <span>+91 9959365158</span>
                        </a>
                        <span className="text-gray-600">|</span>
                        <a href="tel:+917330676456" className="flex items-center gap-1 hover:text-[#2E63E8] transition-colors">
                            <FiPhone size={12} />
                            <span>+91 7330676456</span>
                        </a>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="mailto:Bhavana@blivdesigns.com" className="flex items-center gap-1 hover:text-[#2E63E8] transition-colors">
                            <FiMail size={12} />
                            <span className="hidden sm:inline">Bhavana@blivdesigns.com</span>
                        </a>
                        <a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:text-pink-500 transition-colors">
                            <FiInstagram size={14} />
                        </a>
                        <a href="https://youtube.com" target="_blank" rel="noreferrer" className="hover:text-red-500 transition-colors">
                            <FiYoutube size={14} />
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Navbar */}
            <nav className={`bg-white/95 backdrop-blur-md border-b border-gray-100 py-1.5 transition-colors duration-300`}>
                <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 flex-shrink-0 transition-transform hover:scale-105 duration-300">
                        <img src={HeaderLogo} alt="Bliv Design Studio" className="h-10 md:h-12 w-auto object-contain" />
                    </Link>

                    {/* Desktop Nav */}
                    <ul className="hidden lg:flex items-center gap-0.5">
                        {dynamicNavLinks.map((link) => (
                            <li key={link.label} className="relative dropdown-trigger">
                                {link.dropdown ? (
                                    <>
                                        <Link
                                            to={link.path}
                                            className={`flex items-center gap-0.5 px-2 py-3.5 text-xs font-semibold transition-colors duration-200
                        ${location.pathname === link.path ? 'text-[#2E63E8]' : 'text-gray-800 hover:text-[#2E63E8]'}`}
                                        >
                                            {link.label}
                                            <FiChevronDown size={12} className="mt-0.5" />
                                        </Link>
                                        <div className="dropdown-menu absolute top-full left-0 mt-0 w-52 bg-white rounded-b-xl shadow-2xl border-t-2 border-[#2E63E8] z-50">
                                            {link.dropdown.map((item) => (
                                                <Link key={item.id} to={`${link.path}?id=${item.id}`} className="block px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 hover:text-[#2E63E8] transition-colors first:rounded-none last:rounded-b-xl">
                                                    {item.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <Link
                                        to={link.path}
                                        className={`block px-2 py-3.5 text-xs font-semibold transition-colors duration-200
                      ${location.pathname === link.path ? 'text-[#2E63E8]' : 'text-gray-800 hover:text-[#2E63E8]'}`}
                                    >
                                        {link.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>

                    {/* CTA + Hamburger */}
                    <div className="flex items-center gap-2">
                        <a href="/contact" className="hidden md:block btn-yellow text-xs px-4 py-2">
                            Get Free Estimate
                        </a>
                        <button
                            onClick={() => setMobileOpen(!mobileOpen)}
                            className="lg:hidden p-1.5 text-gray-800 hover:text-[#2E63E8] transition-colors"
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                <div className={`mobile-menu lg:hidden bg-white border-t border-gray-100 ${mobileOpen ? 'open' : ''}`}>
                    <div className="px-4 py-3 space-y-1">
                        {dynamicNavLinks.map((link) => (
                            <div key={link.label}>
                                {link.dropdown ? (
                                    <>
                                        <div className="flex items-center justify-between w-full px-3 py-2 text-gray-800 hover:text-[#2E63E8] text-xs font-semibold transition-colors">
                                            <Link
                                                to={link.path}
                                                onClick={() => setMobileOpen(false)}
                                                className="flex-grow text-left"
                                            >
                                                {link.label}
                                            </Link>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setOpenDropdown(openDropdown === link.label ? null : link.label);
                                                }}
                                                className="p-1"
                                                aria-label="Toggle dropdown"
                                            >
                                                <FiChevronDown size={12} className={`transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                                            </button>
                                        </div>
                                        {openDropdown === link.label && (
                                            <div className="pl-4 space-y-0.5 border-l border-blue-800 ml-3 mb-2">
                                                {link.dropdown.map((item) => (
                                                    <Link
                                                        key={item.id}
                                                        to={`${link.path}?id=${item.id}`}
                                                        onClick={() => setMobileOpen(false)}
                                                        className="block py-1.5 text-xs text-gray-500 hover:text-[#2E63E8] transition-colors"
                                                    >
                                                        {item.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Link
                                        to={link.path}
                                        className="block px-3 py-2 text-gray-800 hover:text-[#2E63E8] text-xs font-semibold transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                )}
                            </div>
                        ))}
                        <div className="pt-2">
                            <a href="/contact" className="btn-yellow block text-center text-xs">
                                Get Free Estimate
                            </a>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
}
